import { DeliveryType, SearchUseCase } from "@outschool/gql-backend-generated";
import {
  AggregatedUpsellQueryQuery,
  AggregatedUpsellQueryQueryVariables,
  FavouriteListingsQuery,
  FavouriteListingsQueryVariables,
  PersonalizedRecommendationsQuery,
  PersonalizedRecommendationsQueryVariables,
  PopularTutorListingsQuery,
  PopularTutorListingsQueryVariables,
  RecentlyViewedQueryQuery,
  RecentlyViewedQueryQueryVariables,
  SearchListingsQuery,
  SearchListingsQueryVariables,
  StartingSoonQuery,
  StartingSoonQueryVariables
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import {
  ClassPageVisitHistory,
  searchListingsQuery
} from "@outschool/ui-components-website";

import { useActiveLearner } from "../providers/ActiveLearnerProvider";
import { useTimeZone } from "../providers/TimeZoneProvider";
import {
  aggregatedUpsellQuery,
  favoriteListingsQuery,
  personalizedRecommendationsQuery,
  popularTutorListingsQuery,
  recentlyViewedQuery,
  startingSoonQuery
} from "../queries/ParentDashboardFeaturedClassesQuery";
import { useActiveLearnerSearchFilters } from "./useActiveLearnerSearchFilters";

type DateBasedTopicCarousel = {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  topics: string[];
  q?: string;
  deliveryTypes?: DeliveryType[];
  title: string;
  trackingLabel: string;
};

export type FeaturedCategoryQuery =
  | RecentlyViewedQueryQuery
  | AggregatedUpsellQueryQuery
  | FavouriteListingsQuery
  | StartingSoonQuery
  | PersonalizedRecommendationsQuery
  | PopularTutorListingsQuery
  | SearchListingsQuery;

type FeaturedCategoryVariables =
  | RecentlyViewedQueryQueryVariables
  | AggregatedUpsellQueryQueryVariables
  | FavouriteListingsQueryVariables
  | StartingSoonQueryVariables
  | PersonalizedRecommendationsQueryVariables
  | PopularTutorListingsQueryVariables
  | SearchListingsQueryVariables;

export type FeaturedCategory = {
  id: string;
  title: string;
  query: FeaturedCategoryQuery;
  path: string;
  trackingLabel: string;
  data: FeaturedCategoryVariables;
  preload?: boolean;
  activeLearnerUid?: string | null;
};

const useDateBasedTopicCarousels = () => {
  const { t } = useTranslation(
    "client\\components\\loggedInHome\\FeaturedClasses"
  );

  // NOTE: Watch out for apostrophe in the title, display should use curly apostrophe, but we should use straight apostrophe for the query
  const dateBasedTopicCarousels: DateBasedTopicCarousel[] = [
    {
      start: dayjs("02-01", "MM-DD"),
      end: dayjs("02-15", "MM-DD"),
      topics: ["Valentines", "Valentine's"],
      title: t("Valentine’s Day"),
      q: "Valentine's Day",
      trackingLabel: "loggedInValentines"
    },
    {
      start: dayjs("01-31-2022", "MM-DD-YYYY"),
      end: dayjs("02-15-2022", "MM-DD-YYYY"),
      topics: ["Chinese New Year", "Lunar New Year"],
      title: t("Chinese/Lunar New Year"),
      q: "Chinese New Year",
      trackingLabel: "loggedInChineseNewYear"
    },
    {
      start: dayjs("12-01", "MM-DD"),
      end: dayjs("12-31", "MM-DD"),
      topics: ["holiday", "winter", "baking", "Christmas", "reindeer", "santa"],
      title: t("Holiday Cheer"),
      q: "Christmas",
      trackingLabel: "loggedInHomeHolidayCheer"
    },
    {
      start: dayjs("02-01", "MM-DD"),
      end: dayjs("03-01", "MM-DD").subtract(1, "day"), // Leap year handling
      topics: [
        "Black History",
        "Decolonization",
        "African American History",
        "Diaspora",
        "Civil Rights"
      ],
      q: "Black History",
      title: t("Black History Month"),
      trackingLabel: "loggedInBlackHistoryMonth"
    },
    {
      start: dayjs("03-01", "MM-DD"),
      end: dayjs("03-31", "MM-DD"),
      topics: [
        "Women's History",
        "feminism",
        "suffrage",
        "ruth bader ginsburg",
        "lucille ball"
      ],
      q: "Women's History",
      title: t("Women’s History Month"),
      trackingLabel: "loggedInWomensHistoryMonth"
    },
    {
      start: dayjs("03-01", "MM-DD"),
      end: dayjs("03-18", "MM-DD"),
      topics: ["st. patrick's"],
      q: "St. Patrick's",
      title: t("St. Patrick’s Day"),
      trackingLabel: "loggedInStPatrickDay"
    },
    {
      start: dayjs("03-01", "MM-DD"),
      end: dayjs("03-31", "MM-DD"),
      topics: ["Spring Break"],
      q: "Spring Break",
      title: t("Spring Break"),
      trackingLabel: "loggedInSpringBreakCamps"
    },
    {
      start: dayjs("04-06-2021", "MM-DD"),
      end: dayjs("04-30-2021", "MM-DD"),
      topics: [
        "earth day",
        "earth month",
        "earth science",
        "environmental science",
        "endangered",
        "climate",
        "conservation",
        "nature",
        "animals",
        "Earth Sciences",
        "Environmentalism"
      ],
      q: "earth day",
      title: t("Celebrate Earth Month"),
      trackingLabel: "loggedInEarthMonth"
    },
    {
      start: dayjs("06-01", "MM-DD"),
      end: dayjs("06-30", "MM-DD"),
      topics: [
        "LGBT",
        "LGBTQ",
        "LGBTQIA",
        "queer",
        "transgender",
        "trans",
        "stonewall"
      ],
      q: "LGTBQ",
      title: t("Pride Month"),
      trackingLabel: "loggedInCelebratePrideMonth"
    },
    {
      start: dayjs("08-01", "MM-DD"),
      end: dayjs("08-31", "MM-DD"),
      topics: [],
      q: "Back to school",
      title: t("Back to school"),
      trackingLabel: "loggedInBackToOutschool"
    },
    {
      start: dayjs("09-01", "MM-DD"),
      end: dayjs("09-30", "MM-DD"),
      topics: [
        "Spain",
        '"Hispanic Culture"',
        '"Hispanic Heritage Month"',
        '"Latin America"',
        '"Puerto Rico"',
        '"Spanish Culture"'
      ],
      title: t("Hispanic Heritage Month"),
      trackingLabel: "loggedInHispanicHeritageMonth"
    },
    {
      start: dayjs("10-01", "MM-DD"),
      end: dayjs("10-31", "MM-DD"),
      topics: ["Halloween"],
      q: "Halloween",
      title: t("Halloween"),
      trackingLabel: "loggedInHalloweenFun"
    },
    {
      start: dayjs("11-01", "MM-DD"),
      end: dayjs("11-30", "MM-DD"),
      topics: ["Native American", "First nations", "Aboriginal"],
      q: "Native American",
      title: t("Native American Heritage Month"),
      trackingLabel: "loggedInNationalNativeAmericanHeritageMonth"
    },
    {
      start: dayjs("06-01", "MM-DD"),
      end: dayjs("07-06", "MM-DD"),
      topics: [
        "Juneteenth",
        "Black History",
        "Black Independence Day",
        "Emancipation",
        "Decolonized",
        "Liberation Day"
      ],
      q: "Black History",
      title: t("Social Justice & History"),
      trackingLabel: "loggedInJuneteenthClasses"
    }
  ];

  const filteredDateBasedTopicCarousels = dateBasedTopicCarousels.filter(
    ({ start, end }) => dayjs().isBetween(start, end, "day", "[]")
  );

  return filteredDateBasedTopicCarousels;
};

const getActivityUidsFromVisitHistory = ({
  limit = 10
}: {
  limit?: number;
} = {}) => {
  return ClassPageVisitHistory.getList()
    .slice(0, limit)
    .map(x => x.activityUid);
};

const useParentDashboardFeaturedClasses = () => {
  const { t } = useTranslation(
    "client\\components\\loggedInHome\\FeaturedClasses"
  );

  const { timeZone } = useTimeZone();
  const activityUidsFromVisitHistory = getActivityUidsFromVisitHistory();
  const dateBasedTopicCarousels = useDateBasedTopicCarousels();
  const { activeLearner } = useActiveLearner();
  const getActiveLearnerSearchFilters = useActiveLearnerSearchFilters();

  const skipRatingVariables = {
    skipRating: false
  };

  const categories: FeaturedCategory[] = [
    {
      id: "recently_viewed",
      title: t("Recently viewed"),
      query: recentlyViewedQuery,
      path: "featured.recentlyViewed",
      trackingLabel: "loggedInHomeRecentlyViewed",
      data: {
        ...skipRatingVariables,
        activityUids: activityUidsFromVisitHistory,
        withRecentReviews: false,
        activeLearnerSearchFilters: activeLearner
          ? getActiveLearnerSearchFilters(activeLearner)
          : undefined
      } as RecentlyViewedQueryQueryVariables,
      preload: true
    },
    {
      id: "from_your_teachers",
      title: t("From your teachers"),
      query: aggregatedUpsellQuery,
      path: "featured.aggregatedUpsell",
      trackingLabel: "loggedInHomeAggregatedUpsell",
      data: {
        ...skipRatingVariables,
        withRecentReviews: false,
        activeLearnerSearchFilters: activeLearner
          ? getActiveLearnerSearchFilters(activeLearner)
          : undefined
      } as AggregatedUpsellQueryQueryVariables,
      preload: true
    },
    {
      id: "upcoming_favorites",
      title: t("Upcoming favorites"),
      query: favoriteListingsQuery,
      path: "featured.favoriteListings",
      trackingLabel: "loggedInHomeUpcomingFavorites",
      data: {
        ...skipRatingVariables,
        withRecentReviews: false,
        activeLearnerSearchFilters: activeLearner
          ? getActiveLearnerSearchFilters(activeLearner)
          : undefined
      } as FavouriteListingsQueryVariables,
      preload: true
    },
    {
      id: "popular_tutors",
      title: t("Popular tutors"),
      query: popularTutorListingsQuery,
      path: "featured.popularTutorListings",
      trackingLabel: "loggedInHomePopularTutors",
      data: {
        ...skipRatingVariables,
        withRecentReviews: false,
        activeLearnerSearchFilters: activeLearner
          ? getActiveLearnerSearchFilters(activeLearner)
          : undefined
      } as PopularTutorListingsQueryVariables
    },
    {
      id: "like_your_favorites",
      title: t("Like your favorites"),
      query: personalizedRecommendationsQuery,
      path: "featured.personalizedRecommendations",
      trackingLabel: "loggedInHomeClassesYouMayAlsoLike",
      data: {
        ...skipRatingVariables,
        withRecentReviews: false,
        activeLearnerUid: activeLearner?.uid
      } as PersonalizedRecommendationsQueryVariables,
      preload: true
    },
    {
      id: "starting_soon",
      title: t("Starting in 24h"),
      query: startingSoonQuery,
      path: "featured.startingSoon",
      trackingLabel: "loggedInHomeStartingSoon",
      data: {
        ...skipRatingVariables,
        withRecentReviews: false,
        activeLearnerSearchFilters: activeLearner
          ? getActiveLearnerSearchFilters(activeLearner)
          : undefined
      } as StartingSoonQueryVariables
    },
    ...dateBasedTopicCarousels.map(
      ({ topics, deliveryTypes, title, trackingLabel, q }) => ({
        id: `date_based_topic_${title}`,
        title,
        query: searchListingsQuery,
        path: "searchListings.listings",
        trackingLabel: trackingLabel,
        data: {
          ...skipRatingVariables,
          withRecentReviews: false,
          limit: 10,
          searchFilters: {
            ...getActiveLearnerSearchFilters(activeLearner),
            topics,
            q,
            delivery: deliveryTypes,
            timeZone
          },
          pageLocation: "PARENT_DASHBOARD",
          useCase: SearchUseCase.Feed
        } as SearchListingsQueryVariables
      })
    )
  ];

  return categories;
};

export default useParentDashboardFeaturedClasses;
